import ReactMarkdown, { type Options, type Components } from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import remarkGfm from "remark-gfm";
import { ErrorBoundary } from "react-error-boundary";
import { useTrace } from "@/hooks/useTrace";
import { useRef, useEffect, useCallback } from "react";
import { rehypeNormalizeUnknownTags } from "@/lib/rehype-normalize-unknown-tags";
import { rehypeRemoveWhitespace } from "@/lib/rehype-remove-whitespace";

const pkg = process.env.NEXT_PUBLIC_PACKAGE;

interface MarkdownRendererProps extends Options {
  children: string;
  className?: string;
  components?: Components;
}

export const MarkdownRenderer = ({
  children,
  className,
  components,
  ...props
}: MarkdownRendererProps) => {
  const { error: traceError } = useTrace();
  // 使用 useRef 存储 traceError 和 children，避免引用变化导致错误边界重新创建
  const traceErrorRef = useRef(traceError);
  const childrenRef = useRef(children);

  useEffect(() => {
    traceErrorRef.current = traceError;
    childrenRef.current = children;
  }, [traceError, children]);

  // // 使用 useCallback 稳定 onError 回调，避免错误边界重新创建
  // const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
  //   // 上报错误到监控系统
  //   // 使用 try-catch 防止上报错误导致死循环
  //   if (pkg !== 'local') {
  //     try {
  //       traceErrorRef.current({
  //         message: "Markdown 渲染失败，已降级展示",
  //         content: childrenRef.current,
  //         error: {
  //           message: error.message,
  //           name: error.name,
  //           stack: error.stack,
  //           errorInfo: errorInfo.componentStack,
  //         },
  //         timestamp: new Date().toISOString(),
  //       });
  //     } catch (e) {
  //       // 静默处理上报错误，避免死循环
  //     }
  //   }
  // }, []);

  // 使用 useCallback 稳定 fallbackRender，避免错误边界重新创建
  const fallbackRender = useCallback(({ error }: { error: Error }) => {
    // 降级渲染：显示原始文本
    return (
      <div className={className} style={{ whiteSpace: "pre-wrap" }}>
        {childrenRef.current}
      </div>
    );
  }, [className]);

  // 合并默认组件和传入的组件
  const mergedComponents: Components = {
    ...components,
  };

  // 传入插件函数本身（不要调用），并在 rehypeRaw 之前处理标准 HAST
  const rehypePlugins = [
    rehypeRemoveWhitespace,
    rehypeRaw,
    rehypeNormalizeUnknownTags,
    [rehypeKatex],
  ] as const;

  return (
    <ErrorBoundary
      fallbackRender={fallbackRender}
    // onError={handleError}
    >
      <ReactMarkdown
        components={mergedComponents}
        remarkPlugins={[remarkMath, remarkGfm] as any}
        rehypePlugins={rehypePlugins as any}
        {...props}
      >
        {children}
      </ReactMarkdown>
    </ErrorBoundary>
  );
}; 